import React, { useState, useRef, useEffect } from 'react';

/**
 * StageTimeline — the internal-pipeline progress control for the
 * Introductions panel. Stage IS status here.
 *
 * Layout (agreed design):
 *   • A full-width tinted container holds the chevron stage cells and doubles
 *     as a visual separator in the panel.
 *   • Chevrons point left→right through the sequence; clicking one moves the
 *     card. Completed = green, current = blue, upcoming = gray.
 *   • An ellipsis (⋯) menu sits to the right of the timeline with:
 *       Disqualified · Withdrew · Remove
 *     (the old standalone Remove button folds into here.)
 *   • Choosing Disqualified/Withdrew recolors the CURRENT cell in place —
 *     red for disqualified, purple for withdrew — the candidate stays on that
 *     stage cell. No separate status pills.
 *   • Below the container, a reason line:
 *       - with reason  → "Disqualified Reason" / "Withdrawal Reason" + text
 *       - without      → "Disqualified at {Stage} stage" / "Withdrew at {Stage} stage"
 *
 * Props:
 *   stages [{ id, name, stage_type }]  currentStageId  currentStageName
 *   status 'pending'|'approved'|'rejected'|'withdrew'
 *   reason        the saved disqualify/withdraw reason text (optional)
 *   onMoveStage(id, name)
 *   onSetBranch('disqualified'|'withdrew')   opens the reason flow
 *   onRemove()    remove the candidate from this search
 *   busy
 */

const BRANCH_STAGE_NAMES = new Set(['disqualified']);

const StageTimeline = ({
  stages = [], currentStageId, currentStageName, status, reason,
  onMoveStage, onSetBranch, onRemove, busy,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const forward = stages.filter(s => !BRANCH_STAGE_NAMES.has((s.name || '').toLowerCase()));
  const currentIndex = forward.findIndex(s => s.id === currentStageId);

  const isWithdrawn = status === 'withdrew';
  const isDisqualified = status === 'rejected'
    || (currentIndex === -1 && !isWithdrawn && stages.some(s => s.id === currentStageId && BRANCH_STAGE_NAMES.has((s.name || '').toLowerCase())));
  const inBranch = isWithdrawn || isDisqualified;

  const currentLabel = forward[currentIndex]?.name || currentStageName || 'this';

  const choose = (branch) => { setMenuOpen(false); onSetBranch?.(branch); };
  const remove = () => { setMenuOpen(false); onRemove?.(); };

  return (
    <div>
      <div className="flex items-center gap-1">
        {/* No container background or border — just the chevrons. */}
        <div className="flex-1 min-w-0">
          <div className="flex items-stretch">
            {forward.map((s, i) => {
              const done = !inBranch && i < currentIndex;
              const active = i === currentIndex;
              const first = i === 0;
              const last = i === forward.length - 1;

              // Softer, familiar tones: light tints with colored text rather
              // than saturated solid fills. Current cell recolors when the
              // card is in a branch state.
              let fill;
              if (active && isDisqualified) fill = 'bg-red-100 text-red-700 border border-red-200';
              else if (active && isWithdrawn) fill = 'bg-purple-100 text-purple-700 border border-purple-200';
              else if (active) fill = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
              else if (done) fill = 'bg-green-50 text-green-700 border border-green-200 hover:bg-emerald-50 hover:text-emerald-700';
              else fill = 'bg-white text-gray-400 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-700';

              const clip = last
                ? (first ? 'none' : 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 8px 50%)')
                : (first
                    ? 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)'
                    : 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)');

              return (
                <button
                  key={s.id}
                  onClick={() => !active && !busy && onMoveStage?.(s.id, s.name)}
                  disabled={busy || active}
                  title={active ? `Current: ${s.name}` : `Move to ${s.name}`}
                  className={`flex-1 min-w-0 h-6 flex items-center justify-center transition-colors disabled:cursor-default ${fill} ${first ? 'rounded-l-sm' : ''} ${last ? 'rounded-r-sm' : ''}`}
                  style={{
                    clipPath: clip === 'none' ? undefined : clip,
                    marginLeft: first ? 0 : '-6px',
                    paddingLeft: first ? 6 : 12,
                    paddingRight: 6,
                  }}
                >
                  <span className="truncate font-medium" style={{ fontSize: '9px' }}>{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Vertical ellipsis menu: Disqualified / Withdrew / Remove */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            disabled={busy}
            className="w-6 h-8 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            title="More"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
              <button onClick={() => choose('disqualified')}
                className="w-full text-left px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Disqualified
              </button>
              <button onClick={() => choose('withdrew')}
                className="w-full text-left px-3 py-1.5 text-xs text-purple-700 hover:bg-purple-50 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Withdrew
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button onClick={remove}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove from search
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reason line — only when in a branch state. */}
      {inBranch && (
        <div className="mt-1.5">
          {reason && reason.trim() ? (
            <>
              <p className={`text-[10px] font-medium ${isDisqualified ? 'text-red-600' : 'text-purple-600'}`}>
                {isDisqualified ? 'Disqualified Reason' : 'Withdrawal Reason'}
              </p>
              <p className="text-[11px] text-gray-600">{reason}</p>
            </>
          ) : (
            <p className={`text-[11px] ${isDisqualified ? 'text-red-600' : 'text-purple-600'}`}>
              {isDisqualified ? 'Disqualified' : 'Withdrew'} at {currentLabel} stage
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StageTimeline;
